import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {EntityModel} from "@fedeltapointofsale/data-client";
import {EditService} from "../../../framework/entity/edit/edit-page/edit.service";
import {PeriodScheduleRuleData, PeriodScheduleRuleOption} from "../period-schedule.data";
import {FormGroup} from "@angular/forms";
import {EntityFormBuilder} from "../../../framework/entity/edit/entity-form-builder/entity-form-builder";
import {Plugin} from "../../../config";

@Component({
    selector: "wbo-period-schedule-rule-edit",
    templateUrl: "./period-schedule-rule-edit.component.html",
    styleUrls: ["./period-schedule-rule-edit.component.scss"]
})
export class PeriodScheduleRuleEditComponent implements OnInit {
    PeriodScheduleRuleOption = PeriodScheduleRuleOption;
    Plugin = Plugin;

    @Input() rulesPath: string;
    @Input() deletable: boolean = true;
    @Input() readonly: boolean = false;

    @Output() onDelete: EventEmitter<void> = new EventEmitter<void>();

    formGroup: FormGroup;

    private _rule: PeriodScheduleRuleData;
    private singleDateOptionSelected: boolean = false;
    private dateRangeOptionSelected: boolean = false;
    private dateTimeRangeOptionSelected: boolean = false;
    private terminalsOptionSelected: boolean = false;
    private exclTerminalsOptionSelected: boolean = false;
    private membershipTypeIdsOptionSelected: boolean = false;
    private exclMembershipTypeIdsOptionSelected: boolean = false;

    constructor(public model: EntityModel<any>,
                private entityFormBuilder: EntityFormBuilder,
                public editService: EditService) {
    }

    get selectedOptions(): PeriodScheduleRuleOption[] {
        return Object.keys(PeriodScheduleRuleOption)
            .map(option => PeriodScheduleRuleOption[option])
            .filter(option => !isNaN(Number(option)))
            .filter(option => this._rule.optionSelected(option));
    }

    ngOnInit(): void {
        this.formGroup = this.entityFormBuilder.createFormGroupForEntity(this.model, this._rule, undefined, this.rulesPath);
    }

    @Input()
    get rule(): PeriodScheduleRuleData {
        return this._rule;
    }

    set rule(rule: PeriodScheduleRuleData) {
        this._rule = rule;

        this.singleDateOptionSelected = this._rule.optionSelected(PeriodScheduleRuleOption.SINGLE_DATE);
        this.dateRangeOptionSelected = this._rule.optionSelected(PeriodScheduleRuleOption.DATE_RANGE);
        this.dateTimeRangeOptionSelected = this._rule.optionSelected(PeriodScheduleRuleOption.DATE_TIME_RANGE);

        this.terminalsOptionSelected = this._rule.optionSelected(PeriodScheduleRuleOption.TERMINALS);
        this.exclTerminalsOptionSelected = this._rule.optionSelected(PeriodScheduleRuleOption.EXCL_TERMINALS);

        this.membershipTypeIdsOptionSelected = this._rule.optionSelected(PeriodScheduleRuleOption.MEMBERSHIP_TYPES);
        this.exclMembershipTypeIdsOptionSelected = this._rule.optionSelected(PeriodScheduleRuleOption.EXCL_MEMBERSHIP_TYPES);
    }

    delete(): void {
        this.onDelete.emit();
    }

    optionSelected(): void {
        if (!this.singleDateOptionSelected && this._rule.optionSelected(PeriodScheduleRuleOption.SINGLE_DATE)) {
            this.singleDateOptionSelected = true;
            if (this.dateRangeOptionSelected) {
                if (!this._rule.singleLocalDate) {
                    this._rule.singleLocalDate = this._rule.startLocalDate;
                }
                this.dateRangeOptionSelected = false;
                this._rule.removeOption(PeriodScheduleRuleOption.DATE_RANGE);
            } else if (this.dateTimeRangeOptionSelected) {
                if (!this._rule.singleLocalDate) {
                    this._rule.singleLocalDate = this._rule.startLocalDTDate;
                }
                this.dateTimeRangeOptionSelected = false;
                this._rule.removeOption(PeriodScheduleRuleOption.DATE_TIME_RANGE);
            }
        } else if (!this.dateRangeOptionSelected && this._rule.optionSelected(PeriodScheduleRuleOption.DATE_RANGE)) {
            this.dateRangeOptionSelected = true;
            if (this.singleDateOptionSelected) {
                if (!this._rule.startLocalDate) {
                    this._rule.startLocalDate = this._rule.singleLocalDate;
                }
                if (!this._rule.endLocalDate) {
                    this._rule.endLocalDate = this._rule.singleLocalDate;
                }
                this.singleDateOptionSelected = false;
                this._rule.removeOption(PeriodScheduleRuleOption.SINGLE_DATE);
            } else if (this.dateTimeRangeOptionSelected) {
                if (!this._rule.startLocalDate) {
                    this._rule.startLocalDate = this._rule.startLocalDTDate;
                }
                if (!this._rule.endLocalDate) {
                    this._rule.endLocalDate = this._rule.endLocalDTDate;
                }
                this.dateTimeRangeOptionSelected = false;
                this._rule.removeOption(PeriodScheduleRuleOption.DATE_TIME_RANGE);
            }
        } else if (!this.dateTimeRangeOptionSelected && this._rule.optionSelected(PeriodScheduleRuleOption.DATE_TIME_RANGE)) {
            this.dateTimeRangeOptionSelected = true;
            if (this.singleDateOptionSelected) {
                if (!this._rule.startLocalDTDate) {
                    this._rule.startLocalDTDate = this._rule.singleLocalDate;
                }
                if (!this._rule.endLocalDTDate) {
                    this._rule.endLocalDTDate = this._rule.singleLocalDate;
                }
                this.singleDateOptionSelected = false;
                this._rule.removeOption(PeriodScheduleRuleOption.SINGLE_DATE);
            } else if (this.dateRangeOptionSelected) {
                if (!this._rule.startLocalDTDate) {
                    this._rule.startLocalDTDate = this._rule.startLocalDate;
                }
                if (!this._rule.endLocalDTDate) {
                    this._rule.endLocalDTDate = this._rule.endLocalDate;
                }
                this.dateRangeOptionSelected = false;
                this._rule.removeOption(PeriodScheduleRuleOption.DATE_RANGE);
            }
        }

        if (!this.terminalsOptionSelected && this._rule.optionSelected(PeriodScheduleRuleOption.TERMINALS)) {
            this.terminalsOptionSelected = true;
            if (this.exclTerminalsOptionSelected) {
                this.exclTerminalsOptionSelected = false;
                this._rule.removeOption(PeriodScheduleRuleOption.EXCL_TERMINALS);
            }
        } else if (!this.exclTerminalsOptionSelected && this._rule.optionSelected(PeriodScheduleRuleOption.EXCL_TERMINALS)) {
            this.exclTerminalsOptionSelected = true;
            if (this.terminalsOptionSelected) {
                this.terminalsOptionSelected = false;
                this._rule.removeOption(PeriodScheduleRuleOption.TERMINALS);
            }
        }

        if (!this.membershipTypeIdsOptionSelected && this._rule.optionSelected(PeriodScheduleRuleOption.MEMBERSHIP_TYPES)) {
            this.membershipTypeIdsOptionSelected = true;
            if (this.exclMembershipTypeIdsOptionSelected) {
                this.exclMembershipTypeIdsOptionSelected = false;
                this._rule.removeOption(PeriodScheduleRuleOption.EXCL_MEMBERSHIP_TYPES);
            }
        } else if (!this.exclMembershipTypeIdsOptionSelected && this._rule.optionSelected(PeriodScheduleRuleOption.EXCL_MEMBERSHIP_TYPES)) {
            this.exclMembershipTypeIdsOptionSelected = true;
            if (this.membershipTypeIdsOptionSelected) {
                this.membershipTypeIdsOptionSelected = false;
                this._rule.removeOption(PeriodScheduleRuleOption.MEMBERSHIP_TYPES);
            }
        }
    }
}
